/**
 * Candlestick chart using Chart.js bar chart (since chartjs-chart-financial
 * may not be installed yet — this uses a stacked bar approach that works
 * with base Chart.js). Renders OHLCV with volume bars.
 */
import React, { useMemo } from 'react';
import { Box, LinearProgress, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { useCandles } from '../api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, TimeScale, Tooltip, Legend, Filler);

interface CandleChartProps {
  segment: string;
  date: string;
  symbol: string;
  tf: string;
  onTfChange: (tf: string) => void;
}

export default function CandleChart({ segment, date, symbol, tf, onTfChange }: CandleChartProps) {
  const { data, isLoading } = useCandles(segment, date, symbol, tf);

  const chartData = useMemo(() => {
    if (!data?.rows.length) return null;
    const labels = data.rows.map((r) => r.ts);
    return {
      labels,
      datasets: [
        {
          label: 'Close',
          data: data.rows.map((r) => r.c),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59,130,246,0.08)',
          borderWidth: 1.5,
          pointRadius: 0,
          tension: 0.1,
          fill: true,
          yAxisID: 'price',
        },
        {
          label: 'High',
          data: data.rows.map((r) => r.h),
          borderColor: 'rgba(16,185,129,0.3)',
          borderWidth: 0.5,
          pointRadius: 0,
          borderDash: [2, 2],
          yAxisID: 'price',
        },
        {
          label: 'Low',
          data: data.rows.map((r) => r.l),
          borderColor: 'rgba(239,68,68,0.3)',
          borderWidth: 0.5,
          pointRadius: 0,
          borderDash: [2, 2],
          yAxisID: 'price',
        },
      ],
    };
  }, [data]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index' as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (ctx: any) => {
              const idx = ctx[0]?.dataIndex;
              if (idx == null || !data) return '';
              return data.rows[idx].ts;
            },
            label: (ctx: any) => {
              const idx = ctx.dataIndex;
              if (!data) return '';
              const r = data.rows[idx];
              if (ctx.dataset.label === 'Close') {
                return `O:${r.o}  H:${r.h}  L:${r.l}  C:${r.c}  V:${r.v}`;
              }
              return '';
            },
          },
        },
      },
      scales: {
        x: {
          type: 'time' as const,
          time: { unit: tf === '15m' ? 'hour' : 'minute' as any },
          grid: { display: false },
          ticks: { maxTicksLimit: 12, font: { size: 10 } },
        },
        price: {
          position: 'left' as const,
          grid: { color: 'rgba(148,163,184,0.1)' },
          ticks: { font: { size: 10 } },
        },
      },
    }),
    [data, tf],
  );

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography variant="subtitle2" fontWeight={700}>
          {symbol}
        </Typography>
        <ToggleButtonGroup
          value={tf}
          exclusive
          onChange={(_, v) => v && onTfChange(v)}
          size="small"
          sx={{ ml: 'auto' }}
        >
          {['1m', '5m', '15m'].map((t) => (
            <ToggleButton key={t} value={t} sx={{ px: 1.5, py: 0.3, fontSize: 11 }}>
              {t}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {isLoading && <LinearProgress sx={{ borderRadius: 1, mb: 1 }} />}

      {chartData ? (
        <Box height={320}>
          <Line data={chartData} options={options} />
        </Box>
      ) : (
        !isLoading && (
          <Typography variant="body2" color="text.secondary">
            No candle data available.
          </Typography>
        )
      )}
    </Box>
  );
}
